import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as FirebaseUser, getRedirectResult, onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import { User } from '../types';
import { logger } from '../utils/secureLogger';
import { userService } from '../services/UserService';

interface AuthContextType {
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

// ホーム画面に追加して起動した iOS の Web アプリではポップアップの結果が戻らないことがあるため、リダイレクト方式でログインする
const isStandaloneApp = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true);

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const createProvisionalUser = (firebaseUser: FirebaseUser): User => ({
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || '',
    photoURL: firebaseUser.photoURL || '',
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  const loadUserDetails = async (firebaseUser: FirebaseUser): Promise<void> => {
    const userId = firebaseUser.uid;
    try {
      const userData = await userService.getUserFullData(userId, userId);
      if (userData) {
        setCurrentUser(userData);
        return;
      }

      // 新規ユーザーの場合
      const newUser: User = {
        id: userId,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      try {
        await userService.createUser(newUser);
        setCurrentUser(newUser);
      } catch (createError) {
        if (createError instanceof Error && createError.message.includes('User already exists')) {
          const existingUser = await userService.getUserFullData(userId, userId);
          if (existingUser) setCurrentUser(existingUser);
        } else {
          throw createError;
        }
      }
    } catch (error) {
      logger.error('Error loading user details:', error);
    }
  };

  const login = async (): Promise<void> => {
    if (isStandaloneApp()) {
      // 戻ってきた後のユーザー読み込みは onAuthStateChanged が行う
      await signInWithRedirect(auth, googleProvider);
      return;
    }

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userId = result.user.uid;

      try {
        const userData = await userService.getUserFullData(userId, userId);
        if (userData) {
          setCurrentUser(userData);
          setFirebaseUser(result.user);
          return;
        }
      } catch (error) {
        logger.error('Error checking user:', error);
      }

      // 新規ユーザーの場合
      const provisionalUser = createProvisionalUser(result.user);
      setCurrentUser(provisionalUser);
      setFirebaseUser(result.user);
      loadUserDetails(result.user);
    } catch (error: any) {
      logger.error('Sign in failed:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked. Please allow popups and try again');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('This domain is not authorized for authentication');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error occurred. Please check your internet connection');
      } else {
        throw new Error(`Authentication error: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      if (typeof window !== 'undefined') {
        localStorage.setItem('logout_success', 'true');
        window.location.href = '/';
      }
    } catch (error) {
      logger.error('Sign out failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    // リダイレクトログインの失敗を記録する（成功時は onAuthStateChanged がユーザーを反映する）
    getRedirectResult(auth).catch((error) => {
      logger.error('Redirect sign in failed:', error);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseUser(firebaseUser);
      if (firebaseUser) {
        setLoading(true);
        loadUserDetails(firebaseUser).then(() => {
          setLoading(false);
        }).catch(() => {
          setCurrentUser(createProvisionalUser(firebaseUser));
          setLoading(false);
        });
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const refreshUser = async () => {
    if (firebaseUser) {
      await loadUserDetails(firebaseUser);
    }
  };

  const value: AuthContextType = {
    currentUser,
    firebaseUser,
    login,
    logout,
    loading,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
